import { memo, useMemo } from 'react';
import { Platform } from 'react-native';

import { Box, Image, Text } from '@bonanza-social/moonlight';
import { Trans } from '@bonanza-social/i18n';
import { StatusInstructionProps } from './types';

function StatusInstruction({
  totalBoxes,
  claimedBoxes,
  variant,
  statusName,
}: StatusInstructionProps) {
  // If there are no more boxes to collect we want to show the total number
  const statusColorProps = useMemo(
    () =>
      Platform.select({
        ios: { gradient: `darkSurfaceStatus${statusName}` },
        web: { gradient: `darkSurfaceStatus${statusName}` },
        android: { color: `darkSurfaceSolidStatus${statusName}` },
      }),
    [statusName],
  );
  const boxesLeft = useMemo(
    () =>
      totalBoxes - claimedBoxes > 0 ? totalBoxes - claimedBoxes : totalBoxes,
    [totalBoxes, claimedBoxes],
  );

  const transComponents = useMemo(
    () => ({
      status: <Text nested variant="caption-semi" {...statusColorProps} />,
    }),
    [statusColorProps],
  );

  const locKey = useMemo(() => {
    const progress =
      claimedBoxes > 0 && claimedBoxes < totalBoxes
        ? 'inprogress'
        : 'notstarted';
    const numberOfBoxes = boxesLeft === 1 ? 'one' : 'other';
    return `status_instruction_${progress}_${variant}_${numberOfBoxes}`;
  }, [claimedBoxes, totalBoxes, boxesLeft, variant]);

  return (
    <Box
      flex={1}
      alignItems="center"
      alignSelf="center"
      padding="space-300"
      borderRadius="radius-200"
      backgroundColor="mutedBackground"
    >
      <Box flexDirection="row" gap="space-050" marginBottom="space-100">
        {[...new Array(totalBoxes)].map((_, index) => (
          <Image
            key={index}
            width="space-400"
            height="space-400"
            name={
              index < claimedBoxes
                ? 'status.check'
                : 'status.statusBoxPlaceholder'
            }
          />
        ))}
      </Box>
      <Text variant="caption" color="darkSurfacePrimary" textAlign="center">
        <Trans
          ns="status"
          i18nKey={locKey}
          components={transComponents}
          values={{ boxesLeft, status: statusName }}
        />
      </Text>
    </Box>
  );
}

export default memo(StatusInstruction);
